import { withForm } from '@components/ui/form/form';
import { useGetServices } from '@queries/service.query';
import { type MissionAssessmentRequestFormData } from '@schemas/mission-assessment-request.schema';
import { expertMatchingStepSchema } from '@schemas/mission-assessment-request.schema';
import { formOptions, useStore } from '@tanstack/react-form';
import { countries } from '@utils/constants';
import { useMemo } from 'react';

const missionAssessmentRequestFormOptions = formOptions({
  defaultValues: {} as MissionAssessmentRequestFormData,
});

export const ExpertMatchingStep = withForm({
  ...missionAssessmentRequestFormOptions,
  render: function Render({ form }) {
    const { data: services, isLoading: isLoadingServices } = useGetServices();

    const serviceCountryOptions = useMemo(() => {
      return countries.filter((country) => ['US', 'GB', 'AU'].includes(country.code));
    }, []);

    const veteranCountriesOfService = useStore(form.store, (state) => state.values.veteranCountriesOfService);
    const veteranServiceIds = useStore(form.store, (state) => state.values.veteranServiceIds);

    const selectedCountries = useMemo(() => {
      if (!veteranCountriesOfService || veteranCountriesOfService.length === 0) return [];
      return countries.filter((country) => veteranCountriesOfService.includes(country.code));
    }, [veteranCountriesOfService]);

    const veteranServiceOptions = useMemo(() => {
      if (!selectedCountries.length || !services) {
        return [];
      }

      const allServices: Array<{ id: string; name: string; countryCode: string }> = [];

      for (const selectedCountry of selectedCountries) {
        const countryServiceGroup = services.find((service) => service.name === selectedCountry.name);

        if (countryServiceGroup?.subServices) {
          for (const subService of countryServiceGroup.subServices) {
            allServices.push({
              id: subService.id,
              name: `${selectedCountry.code}: ${subService.name}`,
              countryCode: selectedCountry.code,
            });
          }
        }
      }

      return allServices;
    }, [selectedCountries, services]);

    return (
      <div className="space-y-4">
        <div>
          <p className="text-gray-500 text-xs mb-4">Specify expert matching criteria (optional)</p>
        </div>

        <div className="space-y-3">
          <form.AppField
            name="veteranCountriesOfService"
            validators={{
              onChange: expertMatchingStepSchema.shape.veteranCountriesOfService,
            }}
            listeners={{
              onChange: ({ value: newCountries }) => {
                // If countries changed, filter out services that don't belong to selected countries
                const currentServiceIds = veteranServiceIds || [];
                if (currentServiceIds.length > 0 && newCountries && newCountries.length > 0) {
                  const validServiceIds = currentServiceIds.filter((serviceId) => {
                    const service = veteranServiceOptions.find((s) => s.id === serviceId);
                    return service && newCountries.includes(service.countryCode);
                  });
                  if (validServiceIds.length !== currentServiceIds.length) {
                    form.setFieldValue('veteranServiceIds', validServiceIds);
                  }
                } else if (!newCountries || newCountries.length === 0) {
                  form.setFieldValue('veteranServiceIds', []);
                }
              },
            }}
          >
            {(field) => (
              <field.SelectMultipleField
                label="Target Nation"
                options={serviceCountryOptions}
                placeholder="Select Target Nation(s)"
              />
            )}
          </form.AppField>

          <form.AppField
            name="veteranServiceIds"
            validators={{
              onChange: expertMatchingStepSchema.shape.veteranServiceIds,
            }}
          >
            {(field) => (
              <field.SelectMultipleField
                label="Expert Service Background"
                disabled={!veteranCountriesOfService || veteranCountriesOfService.length === 0}
                options={veteranServiceOptions.map((option) => ({ value: option.id, label: option.name }))}
                placeholder={
                  veteranCountriesOfService && veteranCountriesOfService.length > 0
                    ? isLoadingServices
                      ? 'Loading services...'
                      : veteranServiceOptions.length > 0
                        ? 'Select Expert Service Background(s)'
                        : 'No services available for selected countries'
                    : 'Select Target Nation(s) first'
                }
              />
            )}
          </form.AppField>

          <form.AppField name="isStrictMatch">
            {(field) => (
              <field.CheckboxField
                label="Strict Match"
                description="Require experts to exactly match the criteria above"
              />
            )}
          </form.AppField>

          <form.AppField name="message">
            {(field) => (
              <field.TextAreaField
                label="Request Note"
                placeholder="Add any additional context"
                rows={4}
                textareaClassName="bg-gray-50"
              />
            )}
          </form.AppField>
        </div>
      </div>
    );
  },
});
